// Manage content tags (v1.91.0). Pranav, 2026-09-16: "Any way we can have the
// ability to add and delete 'content tags' in short form content projects?"
// The list is company-wide (every content team shares it) and had been frozen
// since the move off Odoo — 43 tags including "prodycts" and "Houe". Anyone on
// a content team can add one or remove one; removing takes it off every card
// it was on, so the count is shown first and the press asks.
import { useEffect, useState } from 'react';
import { Plus, X } from 'lucide-react';
import type { ContentTag } from '@cg-projects/shared';
import { api } from '@/core/api/apiClient';
import { Icon } from '@/core/ui/icons';
import { useEscapeToClose } from '@/core/ui/useEscapeToClose';
import { useLookupStore } from '@/features/lookups/store/lookupStore';

export function ContentTagsModal({ onClose }: { onClose: () => void }) {
  const tags = useLookupStore((s) => s.contentTags);
  const reload = useLookupStore((s) => s.reloadContentTags);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEscapeToClose(onClose);
  useEffect(() => { void reload(); }, [reload]);

  const add = async () => {
    const name = draft.trim();
    if (!name || busy) return;
    setBusy(true); setError(null);
    try {
      await api.post<{ tag: ContentTag }>('/tags/content', { name });
      setDraft('');
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not add that tag.');
    }
    setBusy(false);
  };

  const remove = async (t: ContentTag) => {
    const n = t.usedOn ?? 0;
    const msg = n > 0
      ? `Remove "${t.name}"? It comes off the ${n} card${n === 1 ? '' : 's'} that carry it. This can't be undone.`
      : `Remove "${t.name}"? No card carries it.`;
    if (!window.confirm(msg)) return;
    setBusy(true); setError(null);
    try {
      await api.delete(`/tags/content/${t.id}`);
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not remove that tag.');
    }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="flex max-h-[85vh] w-full max-w-md flex-col rounded-xl border border-line bg-page shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between border-b border-line px-4 py-3">
          <div>
            <h2 className="text-[14px] font-semibold text-ink">Content tags</h2>
            <p className="text-[12px] text-faint">Shared by every content team. Removing a tag takes it off every card.</p>
          </div>
          <button onClick={onClose} className="rounded p-1 text-faint hover:text-ink" title="Close"><Icon i={X} size={16} /></button>
        </div>

        <div className="flex gap-2 border-b border-line px-4 py-3">
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') void add(); }}
            placeholder="New tag, e.g. Green screen"
            maxLength={60}
            className="min-w-0 flex-1 rounded-md border border-line bg-canvas px-2 py-1.5 text-[13px] text-ink outline-none placeholder:text-faint focus:border-brand/60"
          />
          <button onClick={() => void add()} disabled={busy || !draft.trim()}
            className="inline-flex items-center gap-1 rounded-md bg-brand px-3 py-1.5 text-[13px] font-semibold text-page disabled:opacity-50">
            <Icon i={Plus} size={14} /> Add
          </button>
        </div>

        {error && <div className="px-4 pt-2 text-[12px] text-danger">{error}</div>}

        <ul className="flex-1 overflow-y-auto px-2 py-2">
          {tags.map((t) => (
            <li key={t.id} className="flex items-center justify-between gap-2 rounded-md px-2 py-1.5 hover:bg-hover">
              <span className="min-w-0 truncate text-[13px] text-ink">{t.name}</span>
              <span className="flex shrink-0 items-center gap-2">
                <span className="text-[11px] text-faint">{t.usedOn ? `on ${t.usedOn} card${t.usedOn === 1 ? '' : 's'}` : 'unused'}</span>
                <button onClick={() => void remove(t)} disabled={busy} className="rounded p-1 text-faint hover:text-danger disabled:opacity-40" title="Remove this tag everywhere">
                  <Icon i={X} size={14} />
                </button>
              </span>
            </li>
          ))}
          {tags.length === 0 && <li className="px-2 py-4 text-[13px] text-faint">No tags yet — add the first one above.</li>}
        </ul>
      </div>
    </div>
  );
}
